import type { WebsiteAssessmentType } from '../hooks/useWebsiteAssessmentSubmissions';

type DisplayOption = {
  value: string;
  label: string;
};

type DisplayQuestion = {
  id: string;
  prompt: string;
  options: DisplayOption[];
};

export type FormattedAssessmentAnswer = {
  questionNumber: number;
  prompt: string;
  answerLabel: string;
};

function kingsMenQuestion(
  id: string,
  prompt: string,
  optionA: string,
  optionB: string,
  optionC: string,
): DisplayQuestion {
  return {
    id,
    prompt,
    options: [
      { value: 'a', label: optionA },
      { value: 'b', label: optionB },
      { value: 'c', label: optionC },
    ],
  };
}

function spiritualGiftQuestion(id: string, prompt: string): DisplayQuestion {
  return {
    id,
    prompt,
    options: [
      { value: 'a', label: 'Rarely' },
      { value: 'b', label: 'Sometimes' },
      { value: 'c', label: 'Often' },
    ],
  };
}

const ASSESSMENT_QUESTIONS: Record<WebsiteAssessmentType, DisplayQuestion[]> = {
  kingsmen_strengths: [
    kingsMenQuestion(
      'km_q01',
      'When facing a difficult situation, you usually…',
      'Pray, seek wisdom, and stay calm',
      'Ask trusted people for advice',
      'Avoid the situation until later',
    ),
    kingsMenQuestion(
      'km_q02',
      'In brotherhood and friendships, you are most likely to…',
      'Encourage and support others',
      'Stay connected only when needed',
      'Keep to yourself most of the time',
    ),
    kingsMenQuestion(
      'km_q03',
      'When God gives you an assignment or goal, you…',
      'Create a plan and pursue it faithfully',
      'Start strong but lose focus sometimes',
      'Wait for the "perfect moment" to begin',
    ),
    kingsMenQuestion(
      'km_q04',
      'How do you normally handle conflict?',
      'Address it with wisdom and self-control',
      'Let emotions lead the conversation',
      'Shut down or walk away completely',
    ),
    kingsMenQuestion(
      'km_q05',
      'Which statement best describes your prayer life?',
      'Consistent and intentional',
      'Mostly when I need something',
      'Inconsistent or struggling',
    ),
    kingsMenQuestion(
      'km_q06',
      'When it comes to finances and stewardship…',
      'I budget, plan, and think long-term',
      'I try to manage as best I can',
      'I mostly live paycheck to paycheck without a plan',
    ),
    kingsMenQuestion(
      'km_q07',
      'During pressure or stress, you…',
      'Trust God and stay focused',
      'Become frustrated but keep moving',
      'Easily become overwhelmed',
    ),
    kingsMenQuestion(
      'km_q08',
      'A strong man should primarily be known for…',
      'Character and integrity',
      'Success and influence',
      'Toughness and control',
    ),
    kingsMenQuestion(
      'km_q09',
      'When someone corrects you, you usually…',
      'Receive it and reflect on it',
      'Defend yourself first',
      'Get offended or withdraw',
    ),
    kingsMenQuestion(
      'km_q10',
      'How do you approach leadership?',
      'Lead by serving others',
      'Lead only when necessary',
      'Prefer others to take charge',
    ),
    kingsMenQuestion(
      'km_q11',
      'Which best describes your spiritual discipline?',
      'I regularly read the Word and grow intentionally',
      'I grow spiritually in seasons',
      'I know I need improvement in this area',
    ),
    kingsMenQuestion(
      'km_q12',
      'When temptation or distractions arise…',
      'I stand firm and set boundaries',
      'I struggle but try to recover quickly',
      'I often give in without resistance',
    ),
    kingsMenQuestion(
      'km_q13',
      'In your family or community, you are usually…',
      'Dependable and present',
      'Helpful when available',
      'Often disconnected or distracted',
    ),
    kingsMenQuestion(
      'km_q14',
      'What motivates you the most?',
      'Purpose and calling',
      'Recognition and achievement',
      'Comfort and convenience',
    ),
    kingsMenQuestion(
      'km_q15',
      'When working with a team or group…',
      'I encourage unity and collaboration',
      'I focus mainly on my role',
      'I avoid group responsibility when possible',
    ),
    kingsMenQuestion(
      'km_q16',
      'How do you respond to failure?',
      'Learn, grow, and try again',
      'Get discouraged for a while',
      'Quit or lose confidence quickly',
    ),
    kingsMenQuestion(
      'km_q17',
      'Which area best reflects your current strength?',
      'Spiritual growth and wisdom',
      'Leadership and influence',
      'Persistence and survival',
    ),
    kingsMenQuestion(
      'km_q18',
      'What does being "on a mission" mean to you?',
      'Walking in God\'s purpose daily',
      'Reaching personal goals and success',
      'Simply making it through life',
    ),
    kingsMenQuestion(
      'km_q19',
      'When younger men or boys are watching you…',
      'I try to model godly character',
      'I don\'t think about it often',
      'I\'m not ready to lead others yet',
    ),
    kingsMenQuestion(
      'km_q20',
      'At this stage of your life, you want to become…',
      'A man of purpose, discipline, and impact',
      'More successful and respected',
      'More stable and secure',
    ),
  ],
  spiritual_gifts: [
    spiritualGiftQuestion(
      'sg_q01',
      'I enjoy encouraging people when they are discouraged or struggling.',
    ),
    spiritualGiftQuestion(
      'sg_q02',
      'I like organizing people, events, or projects so things run smoothly.',
    ),
    spiritualGiftQuestion('sg_q03', 'People often come to me for wisdom, guidance, or advice.'),
    spiritualGiftQuestion(
      'sg_q04',
      'I enjoy helping behind the scenes without needing recognition.',
    ),
    spiritualGiftQuestion(
      'sg_q05',
      'I feel confident sharing the Gospel or talking to others about Jesus.',
    ),
    spiritualGiftQuestion(
      'sg_q06',
      'I quickly notice when someone has a practical need and want to help.',
    ),
    spiritualGiftQuestion('sg_q07', 'I enjoy teaching and explaining God\'s Word to others.'),
    spiritualGiftQuestion(
      'sg_q08',
      'I am passionate about praying for others and believing God for miracles.',
    ),
    spiritualGiftQuestion(
      'sg_q09',
      'I naturally motivate people to grow spiritually or do better.',
    ),
    spiritualGiftQuestion(
      'sg_q10',
      'I enjoy giving generously to support ministry or people in need.',
    ),
    spiritualGiftQuestion(
      'sg_q11',
      'I feel burdened to care for and spiritually protect people.',
    ),
    spiritualGiftQuestion(
      'sg_q12',
      'I can usually recognize when something is spiritually right or wrong.',
    ),
    spiritualGiftQuestion(
      'sg_q13',
      'I enjoy creating welcoming environments where people feel loved and included.',
    ),
    spiritualGiftQuestion(
      'sg_q14',
      'I am willing to step up and lead when direction is needed.',
    ),
    spiritualGiftQuestion(
      'sg_q15',
      'I enjoy studying Scripture deeply and learning biblical truths.',
    ),
    spiritualGiftQuestion(
      'sg_q16',
      'I am compassionate toward hurting, sick, or broken people.',
    ),
    spiritualGiftQuestion(
      'sg_q17',
      'I enjoy starting new ministries, ideas, or opportunities for outreach.',
    ),
    spiritualGiftQuestion(
      'sg_q18',
      'I remain confident in God even during difficult situations.',
    ),
    spiritualGiftQuestion(
      'sg_q19',
      'I enjoy mentoring or discipling others in their spiritual walk.',
    ),
    spiritualGiftQuestion(
      'sg_q20',
      'I feel fulfilled when serving others more than being in front of people.',
    ),
  ],
};

export function formatAssessmentAnswers(
  assessmentType: WebsiteAssessmentType,
  answers: Record<string, string>,
): FormattedAssessmentAnswer[] {
  const questions = ASSESSMENT_QUESTIONS[assessmentType] ?? [];

  return questions.flatMap((question, index) => {
    const value = answers[question.id];
    if (!value) return [];

    const option = question.options.find((entry) => entry.value === value);
    return [
      {
        questionNumber: index + 1,
        prompt: question.prompt,
        answerLabel: option?.label ?? value.toUpperCase(),
      },
    ];
  });
}
